/**
 * Replaces bpmn-js TextRenderer to truncate labels to 25 chars in the canvas.
 * The full name remains in the model and is shown in the property panel.
 */
import { assign } from 'min-dash';
import TextUtil from 'diagram-js/lib/util/Text';

const MAX_LABEL_LENGTH = 25;
const DEFAULT_FONT_SIZE = 12;
const LINE_HEIGHT_RATIO = 1.2;
const MIN_TEXT_ANNOTATION_HEIGHT = 30;

function truncateLabel(text) {
  if (!text || text.length <= MAX_LABEL_LENGTH) return text;
  return text.slice(0, MAX_LABEL_LENGTH) + '...';
}

export default function TruncatingTextRenderer(config) {
  const defaultStyle = assign({
    fontFamily: 'Arial, sans-serif',
    fontSize: DEFAULT_FONT_SIZE,
    fontWeight: 'normal',
    lineHeight: LINE_HEIGHT_RATIO
  }, (config && config.defaultStyle) || {});

  const fontSize = parseInt(defaultStyle.fontSize, 10) - 1;
  const externalStyle = assign({}, defaultStyle, { fontSize }, (config && config.externalStyle) || {});

  const textUtil = new TextUtil({ style: defaultStyle });

  this.getExternalLabelBounds = function (bounds, text) {
    const truncated = truncateLabel(text);
    const layoutedDimensions = textUtil.getDimensions(truncated, {
      box: { width: 90, height: 30 },
      style: externalStyle
    });
    return {
      x: Math.round(bounds.x + bounds.width / 2 - layoutedDimensions.width / 2),
      y: Math.round(bounds.y),
      width: Math.ceil(layoutedDimensions.width),
      height: Math.ceil(layoutedDimensions.height)
    };
  };

  this.getTextAnnotationBounds = function (bounds, text) {
    const truncated = truncateLabel(text);
    const layoutedDimensions = textUtil.getDimensions(truncated, {
      box: bounds,
      style: defaultStyle,
      align: 'left-top',
      padding: 5
    });
    return {
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: Math.max(MIN_TEXT_ANNOTATION_HEIGHT, Math.round(layoutedDimensions.height))
    };
  };

  this.createText = function (text, options) {
    const truncated = truncateLabel(text);
    return textUtil.createText(truncated, options || {});
  };

  this.getDefaultStyle = () => defaultStyle;
  this.getExternalStyle = () => externalStyle;
}

TruncatingTextRenderer.$inject = ['config.textRenderer'];
